import { Call, SEATS, partnerOf } from "@ravalmatic/bridge-engine/core";
import { BoardManager, BoardPhaseSnapshot, BoardPlaySnapshot } from "./BoardManager";
import { buildBoardPlayViewState } from "./BoardViewState";
import { BiddingSnapshot } from "./BiddingManager";
import { CardsContainer } from "./CardsContainer";
import { GameVars } from "../../GameVars";
import { BoardLayoutManager } from "./BoardLayoutManager";
import { AdversaryContainer } from "./players/AdversaryContainer";
import { PlayerContainer } from "./players/PlayerContainer";
import { BoardPhase, HandMode } from "../../Types";
import { BasePlayerContainer } from "./players/BasePlayerContainer";
import { LOCAL_PLAYER_SEAT, isLocalPlayerSeat } from "./PlayerSeats";
import { AnimationBarrier } from "./BoardAnimations";
import type { AnimationStep } from "./BoardAnimations";

export class BoardContainer extends Phaser.GameObjects.Container {

    private readonly boardManager: BoardManager;
    private readonly layoutManager = new BoardLayoutManager();
    private readonly cardsContainer: CardsContainer;
    private readonly unsubscribeFromBidding: () => void;
    private readonly unsubscribeFromPhase: () => void;
    private readonly unsubscribeFromPlay: () => void;
    private readonly players: BasePlayerContainer[];
    private destroyed = false;
    private player: PlayerContainer;
    private adversaries: AdversaryContainer[];
    private transitionBarrier?: AnimationBarrier;
    private playPhaseInfoOffset = false;

    constructor(
        scene: Phaser.Scene,
        boardManager: BoardManager,
        private readonly hideBiddingMenu: (onComplete: () => void) => void,
        private readonly restoreBackground: (onComplete: () => void) => void,
        private readonly moveHudInfoDown: () => void = () => {}
    ) {

        super(scene);

        this.boardManager = boardManager;
        const state = this.boardManager.getState();

        this.player = new PlayerContainer(scene, state.deal.hands[LOCAL_PLAYER_SEAT]);
        const adversarySeats = SEATS.filter(seat => !isLocalPlayerSeat(seat));
        this.adversaries = adversarySeats.map(seat => new AdversaryContainer(
            scene,
            seat,
            state.deal.hands[seat]
        ));
        this.players = [this.player, ...this.adversaries];
        this.players.forEach(player => this.layoutPlayer(
            player, isLocalPlayerSeat(player.seat) ? HandMode.INTERACTIVE : HandMode.HIDDEN
        ));
        this.player.startBiddingEntrance();
        this.players.forEach(player => player.setPlayEnabled(false));
        this.players.forEach(player => player.setCardSelectedHandler(
            (seat, card) => this.boardManager.submitPlayerCard(seat, card)
        ));
        this.add(this.players);

        this.cardsContainer = new CardsContainer(scene);
        this.add(this.cardsContainer);

        this.unsubscribeFromBidding = this.boardManager.getBiddingManager()
            .subscribe(this.onBiddingStateChanged.bind(this));
        this.unsubscribeFromPhase = boardManager.subscribeToPhase(this.onPhaseChanged.bind(this));
        this.unsubscribeFromPlay = boardManager.subscribeToPlay(this.onPlayStateChanged.bind(this));
    }

    public destroy(fromScene?: boolean): void {

        this.destroyed = true;
        this.transitionBarrier?.cancel();
        this.unsubscribeFromBidding();
        this.unsubscribeFromPhase();
        this.unsubscribeFromPlay();
        super.destroy(fromScene);
    }

    public setAvatarClickHandler(handler: () => void): void {

        this.player.setAvatarClickHandler(handler);
    }

    public setPlayerAvatarId(avatarId: number): void {

        this.player.setAvatarId(avatarId);
    }

    public relayout(): void {

        this.players.forEach(player => {
            player.relayout();
            this.layoutPlayer(player, player.handMode);
        });
        this.cardsContainer.relayout();
    }

    private onPhaseChanged(snapshot: BoardPhaseSnapshot): void {

        if (snapshot.phase !== BoardPhase.TRANSITION) {
            this.transitionBarrier?.cancel();
            this.transitionBarrier = undefined;
        }
        this.players.forEach(player => player.setPlayFeedbackEnabled(snapshot.phase === BoardPhase.PLAY));
        if (snapshot.phase === BoardPhase.RESULT) {
            this.cardsContainer.stopAnimations();
        }
        if (snapshot.phase === BoardPhase.PLAY) {
            this.players.forEach(player => player.showPlayCardinal());
        }
        if (snapshot.phase === BoardPhase.TRANSITION) {
            const transition = snapshot.transition;
            if (!transition) {
                throw new Error("Board transition requires an animation request");
            }
            if (this.transitionBarrier) {
                return;
            }
            const barrier = new AnimationBarrier();
            this.transitionBarrier = barrier;
            if (snapshot.contract) {
                const dummySeat = partnerOf(snapshot.contract.declarer);
                const dummy = this.players.find(player => player.seat === dummySeat);
                if (dummy && !isLocalPlayerSeat(dummy.seat)) {
                    const layout = this.layoutManager.getPlayerLayout(
                        dummy.seat, HandMode.VISIBLE_READONLY, GameVars.gameWidth, GameVars.gameHeight
                    );
                    dummy.applyLayout({ ...layout, hand: { ...layout.hand, duration: 0 } });
                }
            }
            const animations: AnimationStep[] = [
                ...this.players.map(player => (done: () => void) => player.hideBiddingHistory(done)),
                this.hideBiddingMenu,
                this.restoreBackground
            ];
            if (snapshot.contract !== null) {
                animations.push((done: () => void) => {
                    if (!this.playPhaseInfoOffset) {
                        this.playPhaseInfoOffset = true;
                        this.layoutPlayer(this.player, this.player.handMode,
                            GameVars.isLandscape ? 0 : BoardLayoutManager.PORTRAIT_PLAY_INFO_DURATION);
                        this.moveHudInfoDown();
                    }
                    done();
                });
                animations.push(...this.adversaries.map(adversary =>
                    (done: () => void) => adversary.finishBiddingPhase(done)
                ));
            }
            barrier.run(animations, () => {
                this.boardManager.completeTransition(transition);
            });
        }
    }

    private onPlayStateChanged(snapshot: BoardPlaySnapshot): void {

        const viewState = buildBoardPlayViewState(snapshot, {
            showLastHand: GameVars.gameData.showLastHand
        });
        if (viewState === null) {
            this.players.forEach(player => player.render(null));
            return;
        }
        if (!snapshot.contractResult) {
            this.cardsContainer.showTrick(viewState.centerTrick, (seat, card, relativeTo) =>
                this.players.find(player => player.seat === seat)?.getCardPresentation(card, relativeTo)
            );
        }
        this.players.forEach(player => {
            const playerState = viewState.players[player.seat];
            player.render(playerState);
            if (snapshot.contractResult) {
                player.stopHandAnimations();
                player.setHandVisible(false);
            } else {
                this.layoutPlayer(player, playerState.handMode);
            }
        });
        const collection = snapshot.trickCollection;
        if (collection) {
            this.cardsContainer.collectTrick(
                collection,
                {
                    onCollapsed: () => this.sendToBack(this.cardsContainer),
                    onComplete: request => {
                        if (this.destroyed) {
                            return;
                        }
                        this.bringToTop(this.cardsContainer);
                        this.boardManager.completeTrickCollection(request);
                    }
                }
            );
        }
    }

    private layoutPlayer(player: BasePlayerContainer, mode: HandMode, avatarDuration = 0): void {

        const layout = this.layoutManager.getPlayerLayout(
            player.seat, mode, GameVars.gameWidth, GameVars.gameHeight
        );
        if (player === this.player && this.playPhaseInfoOffset && !GameVars.isLandscape) {
            layout.avatar.y += (BoardLayoutManager.PORTRAIT_PLAY_INFO_OFFSET - 15) / GameVars.scaleY;
            layout.avatar.duration = avatarDuration;
        }
        player.applyLayout(layout);
    }

    private onBiddingStateChanged(snapshot: BiddingSnapshot): void {

        const callsBySeat: Call[][] = [[], [], [], []];
        snapshot.calls.forEach(({ seat, call }) => callsBySeat[seat].push(call));
        const bidding = this.boardManager.getPhaseSnapshot().phase === BoardPhase.BIDDING;
        this.players.forEach(player => {
            player.setBiddingTurnActive(bidding && !snapshot.state.isOver
                && (snapshot.isPlayerTurn || snapshot.thinkingSeat !== null)
                && snapshot.state.turn === player.seat);
            player.setBiddingHistory(callsBySeat[player.seat]);
        });
        if (!snapshot.state.isOver) {
            this.adversaries.forEach(adversary => adversary.setBiddingPhase(true));
        }
    }
}
